import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { BRAND } from "@/config/brand";

// Paramètres de la plateforme.
//
// Les informations d'identité réellement prévues par le schéma
// (`parametres_plateforme` : nom_plateforme, email_contact, telephone_contact,
// maintenance_active) sont lues ET écrites dans Supabase par le Super Admin.
// Les préférences sans colonne dédiée (devise, langue, fuseau, format de date,
// notifications, sécurité fine) restent des préférences d'interface : aucune
// colonne n'existe, on n'invente donc aucune donnée.

export interface PlatformGeneralSettings {
  nom: string;
  description: string;
  email: string;
  telephone: string;
  adresse: string;
}

export interface PlatformDisplaySettings {
  devise: string;
  langue: string;
  fuseau: string;
  formatDate: string;
}

export interface PlatformNotificationSettings {
  emailActivation: boolean;
  alerteAbonnement: boolean;
  alertePaiement: boolean;
  rapportHebdo: boolean;
}

export interface PlatformSecuritySettings {
  dureeSession: string;
  doubleAuthentification: boolean;
  journalisation: boolean;
  maintenance: boolean;
}

export interface PlatformSettingsState {
  general: PlatformGeneralSettings;
  plateforme: PlatformDisplaySettings;
  notifications: PlatformNotificationSettings;
  securite: PlatformSecuritySettings;
}

interface PlatformSettingsContextValue {
  settings: PlatformSettingsState;
  updateGeneral: (patch: Partial<PlatformGeneralSettings>) => void;
  updatePlateforme: (patch: Partial<PlatformDisplaySettings>) => void;
  updateNotifications: (patch: Partial<PlatformNotificationSettings>) => void;
  updateSecurite: (patch: Partial<PlatformSecuritySettings>) => void;
  resetSettings: () => void;
}

const PlatformSettingsContext = createContext<PlatformSettingsContextValue | null>(null);

export const DEVISE_OPTIONS = [
  { value: "FCFA", label: "Franc CFA (FCFA)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "USD", label: "Dollar américain (USD)" },
];

export const LANGUE_OPTIONS = [
  { value: "fr", label: "Français" },
  { value: "en", label: "Anglais" },
];

export const FUSEAU_OPTIONS = [
  { value: "Africa/Ouagadougou", label: "Burkina Faso (GMT+0)" },
  { value: "Africa/Abidjan", label: "Côte d'Ivoire (GMT+0)" },
  { value: "Europe/Paris", label: "Europe / Paris (GMT+1)" },
];

export const FORMAT_DATE_OPTIONS = [
  { value: "dd/mm/yyyy", label: "JJ/MM/AAAA" },
  { value: "yyyy-mm-dd", label: "AAAA-MM-JJ" },
];

export const DUREE_SESSION_OPTIONS = [
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 heure" },
  { value: "120", label: "2 heures" },
  { value: "480", label: "8 heures" },
];

function defaultSettings(): PlatformSettingsState {
  return {
    general: {
      nom: BRAND.name,
      description: BRAND.description,
      email: BRAND.email,
      telephone: BRAND.phone,
      adresse: BRAND.address,
    },
    plateforme: {
      devise: BRAND.currency,
      langue: "fr",
      fuseau: "Africa/Ouagadougou",
      formatDate: "dd/mm/yyyy",
    },
    notifications: {
      emailActivation: true,
      alerteAbonnement: true,
      alertePaiement: true,
      rapportHebdo: false,
    },
    securite: {
      dureeSession: "60",
      doubleAuthentification: false,
      journalisation: true,
      maintenance: false,
    },
  };
}

export function PlatformSettingsProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [settings, setSettings] = useState<PlatformSettingsState>(defaultSettings);
  const [rowId, setRowId] = useState<string | null>(null);

  useEffect(() => {
    if (!session || session.role !== "super_admin") {
      setRowId(null);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const { data, error } = await supabase
          .from("parametres_plateforme")
          .select("id, nom_plateforme, email_contact, telephone_contact, maintenance_active")
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (!active || !data) return;
        const row = data as Record<string, unknown>;
        setRowId(typeof row.id === "string" ? row.id : null);
        setSettings((prev) => ({
          ...prev,
          general: {
            ...prev.general,
            nom: typeof row.nom_plateforme === "string" && row.nom_plateforme ? row.nom_plateforme : prev.general.nom,
            email: typeof row.email_contact === "string" && row.email_contact ? row.email_contact : prev.general.email,
            telephone:
              typeof row.telephone_contact === "string" && row.telephone_contact
                ? row.telephone_contact
                : prev.general.telephone,
          },
          securite: { ...prev.securite, maintenance: row.maintenance_active === true },
        }));
      } catch (caught) {
        console.error("[Paramètres plateforme] chargement Supabase échoué", caught);
      }
    })();
    return () => {
      active = false;
    };
  }, [session]);

  const persist = useCallback(
    (patch: Record<string, unknown>) => {
      if (!rowId) return;
      void supabase
        .from("parametres_plateforme")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", rowId)
        .then(({ error }) => {
          if (error) {
            dispatchDataError({
              scope: "Paramètres",
              message: "L'enregistrement des paramètres de la plateforme a échoué.",
              technical: describeSupabaseError(error),
            });
          }
        });
    },
    [rowId]
  );

  const updateGeneral = useCallback(
    (patch: Partial<PlatformGeneralSettings>) => {
      setSettings((prev) => ({ ...prev, general: { ...prev.general, ...patch } }));
      const columns: Record<string, unknown> = {};
      if (patch.nom !== undefined) columns.nom_plateforme = patch.nom;
      if (patch.email !== undefined) columns.email_contact = patch.email;
      if (patch.telephone !== undefined) columns.telephone_contact = patch.telephone;
      if (Object.keys(columns).length > 0) persist(columns);
    },
    [persist]
  );

  const updatePlateforme = useCallback((patch: Partial<PlatformDisplaySettings>) => {
    setSettings((prev) => ({ ...prev, plateforme: { ...prev.plateforme, ...patch } }));
  }, []);

  const updateNotifications = useCallback((patch: Partial<PlatformNotificationSettings>) => {
    setSettings((prev) => ({ ...prev, notifications: { ...prev.notifications, ...patch } }));
  }, []);

  const updateSecurite = useCallback(
    (patch: Partial<PlatformSecuritySettings>) => {
      setSettings((prev) => ({ ...prev, securite: { ...prev.securite, ...patch } }));
      if (patch.maintenance !== undefined) persist({ maintenance_active: patch.maintenance });
    },
    [persist]
  );

  const resetSettings = useCallback(() => setSettings(defaultSettings()), []);

  const value = useMemo(
    () => ({ settings, updateGeneral, updatePlateforme, updateNotifications, updateSecurite, resetSettings }),
    [settings, updateGeneral, updatePlateforme, updateNotifications, updateSecurite, resetSettings]
  );

  return <PlatformSettingsContext.Provider value={value}>{children}</PlatformSettingsContext.Provider>;
}

export function usePlatformSettings(): PlatformSettingsContextValue {
  const context = useContext(PlatformSettingsContext);
  if (!context) {
    throw new Error("usePlatformSettings doit être utilisé à l'intérieur de PlatformSettingsProvider");
  }
  return context;
}